<template>
  <el-drawer
    :visible.sync="innerVisible"
    direction="rtl"
    size="480px"
    :with-header="false"
    custom-class="org-quota-drawer"
    @open="loadPanel"
  >
    <div v-loading="loading" class="drawer-wrap">
      <template v-if="org">
        <div class="drawer-head">
          <div>
            <div class="title"><i class="el-icon-s-data"></i> 配额设置</div>
            <div class="sub">{{ org.orgName }}</div>
          </div>
          <el-button type="text" icon="el-icon-close" @click="innerVisible = false" />
        </div>

        <div v-for="item in items" :key="item.key" class="quota-card">
          <div class="card-top">
            <span class="dot" :style="{ background: item.color }"></span>
            <div class="card-meta">
              <div class="card-title">{{ item.title }}</div>
              <div class="card-num">{{ item.used }} / {{ item.max }} {{ item.unit }}</div>
            </div>
            <span class="pct">{{ item.percent }}%</span>
            <el-button type="text" icon="el-icon-edit" @click="editItem(item)" />
          </div>
          <el-progress :percentage="item.percent" :color="item.color" :show-text="false" />
          <div class="card-foot">已使用 {{ item.used }} {{ item.unit }}，剩余 {{ item.remain }} {{ item.unit }}可用</div>
        </div>

        <div class="drawer-tip">配额修改后立即生效，请谨慎操作</div>
        <div class="drawer-foot">
          <el-button @click="resetDefault">重置默认</el-button>
          <el-button @click="innerVisible = false">取消</el-button>
          <el-button type="primary" icon="el-icon-check" @click="save">保存设置</el-button>
        </div>
      </template>
    </div>

    <el-dialog title="修改配额上限" :visible.sync="editVisible" width="400px" append-to-body>
      <el-form v-if="editItemRef" label-width="100px" size="small">
        <el-form-item label="配额项">{{ editItemRef.title }}</el-form-item>
        <el-form-item label="上限">
          <el-input v-model.number="editMax" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" @click="applyEdit">确定</el-button>
      </span>
    </el-dialog>
  </el-drawer>
</template>

<script>
import { gasApi } from '@/api/gas'

export default {
  name: 'OrgQuotaDrawer',
  props: {
    visible: { type: Boolean, default: false },
    orgId: { type: [Number, String], default: null }
  },
  data() {
    return {
      loading: false,
      org: null,
      quota: {},
      items: [],
      editVisible: false,
      editItemRef: null,
      editMax: 0
    }
  },
  computed: {
    innerVisible: {
      get() { return this.visible },
      set(v) { this.$emit('update:visible', v) }
    }
  },
  watch: {
    orgId() {
      if (this.innerVisible) this.loadPanel()
    }
  },
  methods: {
    async loadPanel() {
      if (!this.orgId) return
      this.loading = true
      try {
        const res = await gasApi.org.quotaPanel(this.orgId)
        const d = res.data || {}
        this.org = d.org
        this.quota = d.quota || {}
        this.items = (d.items || []).filter(it => it.key !== 'storage').map(it => ({ ...it }))
      } finally {
        this.loading = false
      }
    },
    editItem(item) {
      this.editItemRef = item
      this.editMax = item.max
      this.editVisible = true
    },
    applyEdit() {
      if (!this.editItemRef) return
      if (this.editMax === null || this.editMax === undefined || this.editMax === '') {
        return this.$message.warning('请输入配额上限')
      }
      if (this.editMax < 0) {
        return this.$message.warning('配额上限不能小于 0')
      }
      const key = this.editItemRef.key
      const u = this.editItemRef.used || 0
      if (this.editMax < u) {
        return this.$message.warning(`当前已使用 ${u}，上限不能小于已使用量`)
      }
      const maxMap = {
        terminals: 'maxTerminals',
        devices: 'maxDevices',
        iotCards: 'maxIotCards',
        users: 'maxUsers',
        alarmRules: 'maxAlarmRules',
        reports: 'maxReports'
      }
      const field = maxMap[key]
      if (field) this.$set(this.quota, field, this.editMax)
      this.editItemRef.max = this.editMax
      this.editItemRef.percent = Math.min(100, Math.round(u * 100 / (this.editMax || 1)))
      this.editItemRef.remain = Math.max(0, this.editMax - u)
      this.editVisible = false
    },
    resetDefault() {
      this.quota = {
        ...this.quota,
        maxTerminals: 200,
        maxDevices: 200,
        maxIotCards: 100,
        maxUsers: 50,
        maxAlarmRules: 100,
        maxReports: 50
      }
      this.items.forEach(it => {
        const defaults = { terminals: 200, devices: 200, iotCards: 100, users: 50, alarmRules: 100, reports: 50 }
        it.max = defaults[it.key] || it.max
        it.percent = Math.min(100, Math.round((it.used || 0) * 100 / (it.max || 1)))
        it.remain = Math.max(0, it.max - (it.used || 0))
      })
      this.$message.info('已恢复默认上限')
    },
    async save() {
      await gasApi.org.quotaPanelSave({ ...this.quota, orgId: this.orgId })
      this.$message.success('配额设置已保存')
      this.$emit('saved')
      this.innerVisible = false
    }
  }
}
</script>

<style scoped>
.drawer-wrap {
  padding: 20px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}
.drawer-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
.title {
  font-size: 17px;
  font-weight: 600;
}
.sub {
  font-size: 14px;
  color: #86909c;
  margin-top: 4px;
}
.quota-card {
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.card-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.card-meta {
  flex: 1;
  min-width: 0;
}
.card-title {
  font-weight: 500;
  font-size: 14px;
}
.card-num {
  font-size: 13px;
  color: #4e5969;
  margin-top: 2px;
}
.pct {
  font-size: 13px;
  color: #86909c;
}
.card-foot {
  font-size: 12px;
  color: #86909c;
  margin-top: 6px;
}
.drawer-tip {
  font-size: 12px;
  color: #ff7d00;
  margin: 16px 0;
}
.drawer-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #e5e6eb;
}
</style>
